import mixpanel from 'mixpanel-browser';
import { StoreModel } from '@/models/store';
import { isEmbedded } from '@/utils/shopifyBridge';

interface MixpanelUserProperties {
  store_id: number | string;
  shopify_store_name: string | null;
  country_name: string | null;
  created_at: string | null;
  currency: string | null;
  domain: string | null;
  email: string | null;
  myshopify_domain: string | null;
  name: string | null;
  phone: string | null;
  plan_display_name: string | null;
  shop_owner: string | null;
  timezone: string | null;
  current_package_name: string | null;
  current_package_id: number | null;
  product_category: string | null;
  role: string | null;
  purpose: string | null;
  is_discount: boolean;
  price: number;
  billing_circle_count: number;
  installed_at: string | null;
  is_data_complete: boolean;
}

export class MixpanelInitializer {

  /**
   * Initialize Mixpanel with store data
   * @param store - The store model containing merchant data
   * @param options - Additional options for initialization
   * @returns boolean indicating success
   */
  public static initialize(store: StoreModel, options: { debug?: boolean } = {}): boolean {
    try {
      // Validate store exists
      if (!store) {
        return false;
      }

      // Detect if app is running in Shopify embedded mode (iframe)
      const isEmbeddedMode = isEmbedded();

      // Initialize Mixpanel if not already initialized
      // Check both our flag and if mixpanel SDK is already loaded
      const isMixpanelLoaded = mixpanel && typeof mixpanel.track === 'function';

      if (!this.isInitialized && !isMixpanelLoaded) {
        // Get token from environment variable
        const token = this.getMixpanelToken();
        if (!token) {
          console.error('[MixpanelInitializer] MIXPANEL_TOKEN not found in environment variables');
          return false;
        }

        const isProd = process.env.NODE_ENV === 'production';
        mixpanel.init(token, {
          debug: options.debug || false,
          ignore_dnt: !isProd, // respect DNT in production
          // Disable persistence in embedded mode to avoid third-party cookie/storage blocking
          disable_persistence: isEmbeddedMode,
          loaded: () => {
            //console.log(`[MixpanelInitializer] Mixpanel loaded successfully (embedded: ${isEmbeddedMode}, persistence: ${!isEmbeddedMode ? 'enabled' : 'disabled'})`);
          },
        });
        this.isInitialized = true;
      } else if (isMixpanelLoaded && !this.isInitialized) {
        // Mixpanel was initialized elsewhere (e.g., App.vue early init)
        // Mark as initialized to avoid re-init attempts
        this.isInitialized = true;
      }

      // Validate and identify user
      const storeId = this.getStoreId(store);
      if (!storeId) {
        //console.error('[MixpanelInitializer] Store ID is missing - cannot identify user');
        return false;
      }

      // Identify the user with store ID
      mixpanel.identify(String(storeId));
      //console.log(`[MixpanelInitializer] Identified user with store ID: ${storeId}`);

      // Build and set user properties
      const userProperties = this.buildUserProperties(store);

      // Only set properties with PII if consent is given
      if (this.hasUserConsent()) {
        mixpanel.people.set(userProperties);
      } else {
        // Set only non-PII properties
        const nonPiiProperties = this.filterPiiProperties(userProperties);
        mixpanel.people.set(nonPiiProperties);
      }

      // Track initialization event
      mixpanel.track('Mixpanel Initialized', {
        store_id: storeId,
        is_data_complete: userProperties.is_data_complete,
        is_embedded_mode: isEmbeddedMode,
        persistence_enabled: !isEmbeddedMode,
        timestamp: new Date().toISOString(),
      });

      //console.log(`[MixpanelInitializer] Initialization complete - Store ID: ${storeId}, Embedded: ${isEmbeddedMode}`);
      return true;

    } catch (error) {
      //console.error('[MixpanelInitializer] Failed to initialize:', error);
      // Log error to Mixpanel if possible
      try {
        mixpanel.track('Mixpanel Initialization Error', {
          error: String(error),
          store_id: store?.id || 'unknown',
          timestamp: new Date().toISOString(),
        });
      } catch (trackError) {
        // Silently fail tracking
      }
      return false;
    }
  }

  /**
   * Update user properties after initialization
   * Respects consent for PII properties
   */
  public static updateUserProperties(properties: Partial<MixpanelUserProperties>): boolean {
    try {
      if (!this.isInitialized) {
        //console.warn('[MixpanelInitializer] Mixpanel not initialized, cannot update properties');
        return false;
      }

      // Only set properties with PII if consent is given
      if (this.hasUserConsent()) {
        mixpanel.people.set(properties);
      } else {
        // Filter out PII properties
        const nonPiiProperties = this.filterPiiProperties(properties as MixpanelUserProperties);
        mixpanel.people.set(nonPiiProperties);
      }

      //console.log('[MixpanelInitializer] User properties updated');
      return true;

    } catch (error) {
      //console.error('[MixpanelInitializer] Failed to update properties:', error);
      return false;
    }
  }

  /**
   * Track custom event
   */
  public static trackEvent(eventName: string, properties?: Record<string, any>): boolean {
    try {
      if (!this.isInitialized) {
        //console.warn('[MixpanelInitializer] Mixpanel not initialized, cannot track event');
        return false;
      }

      mixpanel.track(eventName, {
        timestamp: new Date().toISOString(),
        ...properties,
      });
      return true;

    } catch (error) {
      //console.error(`[MixpanelInitializer] Failed to track event ${eventName}:`, error);
      return false;
    }
  }

  /**
   * Reset Mixpanel (useful for logout)
   */
  public static reset(): void {
    try {
      if (this.isInitialized) {
        mixpanel.reset();
        this.isInitialized = false;
        //console.log('[MixpanelInitializer] Mixpanel reset successfully');
      }
    } catch (error) {
      //console.error('[MixpanelInitializer] Failed to reset Mixpanel:', error);
    }
  }

  /**
   * Get initialization status
   */
  public static getInitializationStatus(): {
    isInitialized: boolean;
    distinctId: string | null;
  } {
    try {
      // Even if isInitialized is true, mixpanel.get_distinct_id() can throw if mixpanel's internal state isn't ready
      const distinctId = this.isInitialized ? mixpanel.get_distinct_id() : null;
      return {
        isInitialized: this.isInitialized,
        distinctId: distinctId,
      };
    } catch (error) {
      // If get_distinct_id() throws, mixpanel isn't ready yet
      return {
        isInitialized: false,
        distinctId: null,
      };
    }
  }

  /**
   * Set super properties that will be sent with every event
   */
  public static setSuperProperties(properties: Record<string, any>): boolean {
    try {
      if (!this.isInitialized) {
        //console.warn('[MixpanelInitializer] Mixpanel not initialized, cannot set super properties');
        return false;
      }

      mixpanel.register(properties);
      //console.log('[MixpanelInitializer] Super properties set');
      return true;

    } catch (error) {
      //console.error('[MixpanelInitializer] Failed to set super properties:', error);
      return false;
    }
  }
  private static isInitialized = false;

  /**
   * Build user properties object with safe property access
   */
  private static buildUserProperties(store: StoreModel): MixpanelUserProperties {
    // Safely access nested properties with defaults
    const information = store.information || {};
    const currentPackage = store.currentPackage || {};
    const config = store.config || {};

    // Calculate billing circle count safely
    const billingCircleCount = this.calculateBillingCircleCount(store.installedAt);

    // Check if all critical data is present
    const isDataComplete = Boolean(
      store.id &&
      store.shopName &&
      information.email &&
      currentPackage.name,
    );

    return {
      // Core identifiers
      store_id: store.id || 'unknown',
      shopify_store_name: store.shopName || null, // Fixed typo from original

      // Store information
      country_name: information.country_name || null,
      created_at: information.created_at || null,
      currency: information.currency || null,
      domain: information.domain || null,
      email: information.email || null,
      myshopify_domain: information.myshopify_domain || null,
      name: information.name || null,
      phone: information.phone || null,
      plan_display_name: information.plan_display_name || null,
      shop_owner: information.shop_owner || null,
      timezone: information.timezone || null,

      // Package information
      current_package_name: currentPackage.name || null,
      current_package_id: currentPackage.billingPackageId || null,
      price: this.safeNumber(currentPackage.price, 0),
      is_discount: Boolean(currentPackage.discount),

      // Configuration
      product_category: config.productCategory || null,
      role: config.role || null,
      purpose: config.purpose || null,

      // Calculated fields
      billing_circle_count: billingCircleCount,
      installed_at: store.installedAt || null,

      // Metadata
      is_data_complete: isDataComplete,
    };
  }

  /**
   * Calculate billing circle count safely
   */
  private static calculateBillingCircleCount(installedAt?: string): number {
    try {
      if (!installedAt) {
        //console.warn('[MixpanelInitializer] installedAt is missing, defaulting billing circle to 0');
        return 0;
      }

      const installedDate = Date.parse(installedAt);
      if (isNaN(installedDate)) {
        //console.warn(`[MixpanelInitializer] Invalid installedAt date: ${installedAt}`);
        return 0;
      }

      const now = Date.now();
      const daysSinceInstall = Math.floor((now - installedDate) / (1000 * 60 * 60 * 24));

      // Billing circles start after 7-day trial
      const billableDays = Math.max(0, daysSinceInstall - 7);
      const circleCount = Math.ceil(billableDays / 30);

      return Math.max(0, circleCount); // Ensure non-negative

    } catch (error) {
      //console.error('[MixpanelInitializer] Error calculating billing circle:', error);
      return 0;
    }
  }

  /**
   * Get store ID safely
   */
  private static getStoreId(store: StoreModel): string | null {
    if (!store.id) {
      return null;
    }
    // Ensure it's a valid ID (not 0, null, undefined, etc.)
    if (store.id <= 0) {
      //console.warn(`[MixpanelInitializer] Invalid store ID: ${store.id}`);
      return null;
    }
    return String(store.id);
  }

  /**
   * Safely convert value to number
   */
  private static safeNumber(value: any, defaultValue: number = 0): number {
    if (value === null || value === undefined) {
      return defaultValue;
    }
    const num = Number(value);
    return isNaN(num) ? defaultValue : num;
  }

  /**
   * Get Mixpanel token from environment variables
   */
  private static getMixpanelToken(): string | null {
    return process.env.VUE_APP_MIXPANEL_TOKEN ||
           process.env.MIXPANEL_TOKEN ||
           null;
  }

  /**
   * Check if user has given consent for PII tracking
   * Respects DNT in production and requires explicit consent
   */
  private static hasUserConsent(): boolean {
    const isProd = process.env.NODE_ENV === 'production';

    // In production, respect Do Not Track
    if (isProd && typeof navigator !== 'undefined' && navigator.doNotTrack === '1') {
      return false;
    }

    // Check for explicit user consent (localStorage)
    try {
      const consent = localStorage.getItem('mixpanel_consent');
      return consent === 'true';
    } catch (error) {
      // localStorage might not be available (SSR, privacy mode, etc.)
      return false;
    }
  }

  /**
   * Filter out PII properties when consent is not given
   * Removes: email, phone, name, shop_owner, myshopify_domain, domain
   */
  private static filterPiiProperties(properties: MixpanelUserProperties): Partial<MixpanelUserProperties> {
    const {
      email,
      phone,
      name,
      shop_owner,
      myshopify_domain,
      domain,
      ...nonPiiProperties
    } = properties;

    return nonPiiProperties;
  }
}

// Export convenience function for simple initialization
export function initializeMixpanel(store: StoreModel, options?: { debug?: boolean }): boolean {
  return MixpanelInitializer.initialize(store, options);
}
